import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { CogIcon } from '../common/icons';
import type { Project } from '../../types';

/**
 * The subject's name at the top of its page: click it to rename, and the cog
 * opens the subject's settings. Both used to be reachable only by typing the
 * settings URL - nothing in the app linked there.
 */
export default function SubjectName({
  project,
  onRenamed,
}: {
  project: Project;
  onRenamed: (project: Project) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const start = () => {
    setDraft(project.name);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const name = draft.trim();
    if (!name || name === project.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      onRenamed(await api.updateProject(project.id, { name }));
      setEditing(false);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rename failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            disabled={saving}
            aria-label="Subject name"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void save()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void save();
              } else if (e.key === 'Escape') {
                setEditing(false);
                setError(null);
              }
            }}
            className="min-w-0 flex-1 text-[17px] font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 border border-blue-400 rounded px-1 -mx-1 focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={start}
            title="Rename this subject"
            className="min-w-0 text-left text-[17px] font-semibold text-gray-900 dark:text-gray-100 truncate rounded hover:bg-gray-100 dark:hover:bg-gray-700 px-1 -mx-1"
          >
            {project.name}
          </button>
        )}
        <Link
          to={`/projects/${project.id}/settings/general`}
          title="Subject settings"
          aria-label="Subject settings"
          className="shrink-0 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <CogIcon />
        </Link>
      </div>
      {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
    </div>
  );
}
